import React, { useEffect, useRef, useState } from 'react';
import { ApiTheme, Theme } from 'interfaces/theme.interface';
import { HelpIcon } from '@common_components/icons/help.icon';
import { EventManagerService } from 'services/eventManagers.service';
import { Client, CommonClientService, CommonGlobalService, CommonSectorService, CommonSiteService, CommonUtilsService, dayjs, FileIcon, FilterIcon, FORMAT_DATE_API, FilterPagination, GroupedOption, ListPaginationResult, LoggerService, Option, RootState, Sector, Site, UserFilter, UUID } from '@umanforge/common';
import { UsersService } from 'services/users.service';
import { DEFAULT_BACKLOG } from 'constants/eventManager.constant';
import { useDispatch, useSelector } from 'react-redux';
import SubmitButton from '@common_components/buttons/submitButton.component';
import { ThemeService } from 'services/themes.service';
import { useTranslation } from 'react-i18next';
import Page from '@common_components/global/page.component';
import { DataExportFilters } from 'interfaces/types.interface';
import { GlobalService } from 'services/global.service';
import { DataExportService } from 'services/exportData.service';
import SelectInput from '@common_components/inputs/selects/selectInput.component';
import DataChecker from 'components/common/dataChecker';
import { AdminTableWithSitesApiResponse, Participant } from 'interfaces/eventManager.interface';
import { EventExported } from 'interfaces/exportData.interface';
import { Loading } from '@common_components/loading.component';
import EngagementModal from 'components/modals/engagements/engagementModal.component';
import { EventCommitment } from 'interfaces/types.interface';
import { FilterPanel } from 'components/FilterSection/filterPanel.component';
import { FilterButton } from 'components/FilterSection/filterButton.component';
import { FilterProvider } from 'components/FilterSection/filterProvider';
import SavedViewsManager from 'components/extraction/savedViewsManager.component';
import EventsTable from 'components/extraction/eventsTable.component';
import ExtractionFiltersPanel from 'components/extraction/filtersPanel.component';
import { useFilter } from 'components/FilterSection/filterProvider';
import { ExtractionSavedView, ExtractionSavedViewColumnInput, ExtractionSavedViewFilter, ExtractionSavedViewFilterInput } from '../interfaces/extractionSavedView.interface';
import { EventTableColumnKey, EVENTS_TABLE_COLUMNS_META } from '../constants/extraction.constants';

const FILTER_WIDTH = 'w-full lg:w-[350px]';

const logger = new LoggerService( 'ExtractionView' );

// Caches for in-flight user loading requests to avoid duplicate /users calls
const participantsInFlightCache = new Map<string, Promise<ListPaginationResult<Participant>>>();
const usersByEventManagersInFlightCache = new Map<string, Promise<Participant[]>>();

function AlertBox( { color, icon, message }: { color: string; icon: React.ReactNode; message: string } ): JSX.Element {
  return (
    <div className={`bg-${color}-100 flex gap-4 py-3 pl-4 border-l-4 border-${color}-600 text-${color}-600`}>
      {icon}
      <p className="font-texte text-lg">{message}</p>
    </div>
  );
}

interface SavedViewsWithFiltersProps {
  buildCurrentFiltersAndColumns: () => { filters: ExtractionSavedViewFilterInput[]; columns: ExtractionSavedViewColumnInput[] };
  applySavedView: ( view: ExtractionSavedView ) => void;
  selectedSavedViewId: string | null;
  onChangeSelectedViewId: ( id: string | null ) => void;
  onClearFilters: () => void;
}

const buildThemesOptions = (
  themes: Theme[],
  allSites: Site[],
  t: ( key: string ) => string,
): GroupedOption<ApiTheme>[] => {
  if ( themes.length === 0 ) {
    return [];
  }

  const themesBySite = new Map<string | null, ApiTheme[]>();
  const allowedSiteIdsSet = new Set( allSites.map( site => { return site.id; } ) );

  themes.forEach( theme => {
    const themeSiteIds = theme.siteIds ?? [];

    const apiTheme: ApiTheme = {
      id: theme.id,
      name: theme.name,
      color: theme.color,
      eventManagerId: undefined,
    };

    // Filtrer les sites auxquels l'utilisateur n'a pas accès
    const visibleSiteIds = themeSiteIds
      .map( rawSiteId => { return rawSiteId as unknown as string; } )
      .filter( siteId => { return allowedSiteIdsSet.has( siteId ); } );

    // Thème sans site visible : groupe "global"
    if ( visibleSiteIds.length === 0 ) {
      const existing = themesBySite.get( null ) ?? [];
      if ( !existing.some( themeItem => { return themeItem.id === apiTheme.id; } ) ) {
        existing.push( apiTheme );
        themesBySite.set( null, existing );
      }
      return;
    }

    // Thème lié à un ou plusieurs sites : une entrée dans chaque groupe de site
    visibleSiteIds.forEach( siteId => {
      const existing = themesBySite.get( siteId ) ?? [];
      if ( !existing.some( themeItem => { return themeItem.id === apiTheme.id; } ) ) {
        existing.push( apiTheme );
        themesBySite.set( siteId, existing );
      }
    } );
  } );

  const result: GroupedOption<ApiTheme>[] = [];

  themesBySite.forEach( ( apiThemes, siteId ) => {
    const options = CommonGlobalService.toOptions( apiThemes );
    CommonUtilsService.sortByKey( options, 'label', 'asc' );

    const label = siteId
      ? ( allSites.find( site => { return site.id === siteId; } )?.name ?? t( 'common:error.missing_site' ) )
      : t( 'common:themes' );

    result.push( {
      label,
      options,
    } );
  } );

  return result;
};

const buildEventManagersOptions = (
  eventManagersFetched: AdminTableWithSitesApiResponse[],
  selectedClients: Option<Client>[],
  selectedSites: Option<Site>[],
  selectedSectors: Option<Sector>[],
  allSites: Site[],
  allSectors: Sector[],
  grouping: 'client' | 'site' | 'sector' | 'table',
  t: ( key: string ) => string,
): GroupedOption<AdminTableWithSitesApiResponse>[] => {
  let allowedSiteIds: string[] | null = null;
  let allowedSectorIds: string[] | null = null;

  if ( selectedSectors.length > 0 ) {
    allowedSectorIds = Array.from( new Set( selectedSectors.map( sector => { return sector.object.id; } ) ) );
  } else if ( selectedSites.length > 0 ) {
    allowedSiteIds = Array.from( new Set( selectedSites.map( site => { return site.object.id; } ) ) );
  } else if ( selectedClients.length > 0 ) {
    const selectedClientIds = selectedClients.map( client => { return client.object.id; } );
    allowedSiteIds = allSites
      .filter( site => { return selectedClientIds.includes( site.clientId ); } )
      .map( site => { return site.id; } );
  }

  const filteredEventManagers = ( allowedSectorIds || allowedSiteIds )
    ? eventManagersFetched.filter( em => {
      const emSiteIds = em.sites.map( site => { return site.id; } );

      // Si on filtre par secteur, on ne garde que les tableaux qui ont au moins un site présent dans les secteurs sélectionnés.
      if ( allowedSectorIds ) {

        const emSectors: string[] = ( em as unknown as { sectors?: string[] } ).sectors ?? [];
        if ( emSectors.length > 0 ) {
          return emSectors.some( secId => { return allowedSectorIds!.includes( secId ); } );
        }

        const allowedSiteIdsFromSectors = Array.from( new Set( selectedSectors.map( sec => { return sec.object.siteId; } ) ) );
        return emSiteIds.some( siteId => { return allowedSiteIdsFromSectors.includes( siteId ); } );
      }

      // Si on filtre uniquement par client, on ne garde que les tableaux
      // dont tous les sites appartiennent à ces clients sélectionnés.
      if ( allowedSiteIds && selectedClients.length > 0 && selectedSites.length === 0 && selectedSectors.length === 0 ) {
        return emSiteIds.every( siteId => { return allowedSiteIds?.includes( siteId ); } );
      }

      // Sinon, on garde les tableaux qui ont au moins un site autorisé.
      if ( allowedSiteIds ) {
        return emSiteIds.some( siteId => { return allowedSiteIds?.includes( siteId ); } );
      }

      return true;
    } )
    : eventManagersFetched;

  const allEventManagersOptions = CommonGlobalService.toOptions( filteredEventManagers );
  const groupedEventManagers: GroupedOption<AdminTableWithSitesApiResponse>[] = [];

  if ( grouping === 'sector' ) {
    const groupedBySector = CommonGlobalService.groupBy( allEventManagersOptions, option => {
      const firstSite = option.object.sites[ 0 ];
      return firstSite && firstSite.sectors && firstSite.sectors.length > 0 ? firstSite.sectors[ 0 ].id : 'no_sector';
    } );

    for ( const [ sectorId, options ] of Object.entries( groupedBySector ) ) {
      const optionEventManagers = options ?? [];
      CommonUtilsService.sortByKey( optionEventManagers, 'label', 'asc' );
      const sectorName = sectorId === 'no_sector'
        ? t( 'common:error.missing_sector' )
        : ( allSectors.find( s => { return s.id === sectorId; } )?.name ?? t( 'common:error.missing_sector' ) );

      groupedEventManagers.push( {
        label: sectorName,
        options: optionEventManagers
      } );
    }
  } else {
    const groupedBySite = CommonGlobalService.groupBy( allEventManagersOptions, option => {
      const firstSite = option.object.sites[ 0 ];
      return firstSite ? firstSite.id : 'no_site';
    } );

    for ( const [ siteId, options ] of Object.entries( groupedBySite ) ) {
      const optionEventManagers = options ?? [];
      CommonUtilsService.sortByKey( optionEventManagers, 'label', 'asc' );
      const siteName = siteId === 'no_site'
        ? t( 'common:error.missing_site' )
        : ( filteredEventManagers.find( em => { return em.sites.some( site => { return site.id === siteId; } ); } )
          ?.sites.find( site => { return site.id === siteId; } )?.name
          ?? t( 'common:error.missing_site' ) );

      groupedEventManagers.push( {
        label: siteName,
        options: optionEventManagers
      } );
    }
  }

  return groupedEventManagers;
};

interface LeftFiltersColumnProps {
  buildCurrentFiltersAndColumns: () => { filters: ExtractionSavedViewFilterInput[]; columns: ExtractionSavedViewColumnInput[] };
  applySavedView: ( view: ExtractionSavedView ) => void;
  selectedSavedViewId: string | null;
  onChangeSelectedViewId: ( id: string | null ) => void;
  onClearFilters: () => void;
  clientsOption: Option<Client>[];
  sitesOption: GroupedOption<Site>[];
  sectorsOption: GroupedOption<Sector>[];
  eventManagersOption: GroupedOption<AdminTableWithSitesApiResponse>[];
  participantsOptions: ( filter: FilterPagination ) => Promise<ListPaginationResult<Participant>>;
  themesOptions: GroupedOption<ApiTheme>[];
  creatorsOptions: ( filter: FilterPagination ) => Promise<ListPaginationResult<Participant>>;
  selectedClients: Option<Client>[];
  selectedSites: Option<Site>[];
  selectedSectors: Option<Sector>[];
  selectedEventManagers: Option<AdminTableWithSitesApiResponse>[];
  selectedParticipants: Option<Participant>[];
  selectedThemes: Option<ApiTheme>[];
  selectedCreators: Option<Participant>[];
  setSelectedClients: ( value: Option<Client>[] ) => void;
  setSelectedSites: ( value: Option<Site>[] ) => void;
  setSelectedSectors: ( value: Option<Sector>[] ) => void;
  setSelectedEventManagers: ( value: Option<AdminTableWithSitesApiResponse>[] ) => void;
  setSelectedParticipants: ( value: Option<Participant>[] ) => void;
  setSelectedThemes: ( value: Option<ApiTheme>[] ) => void;
  setSelectedCreators: ( value: Option<Participant>[] ) => void;
  creationDate: Date[];
  deliveryDate: Date[];
  archivedDate: Date[];
  completionDate: Date[];
  setCreationDateFunct: ( startDate: Date | null, endDate: Date | null ) => void;
  setDeliveryDateFunct: ( startDate: Date | null, endDate: Date | null ) => void;
  setArchivedDateFunct: ( startDate: Date | null, endDate: Date | null ) => void;
  setCompletionDateFunct: ( startDate: Date | null, endDate: Date | null ) => void;
}

const LeftFiltersColumn: React.FC<LeftFiltersColumnProps> = props => {
  const { isCollapsed } = useFilter();

  if ( isCollapsed ) {
    return null;
  }

  const {
    buildCurrentFiltersAndColumns,
    applySavedView,
    selectedSavedViewId,
    onChangeSelectedViewId,
    onClearFilters,
    clientsOption,
    sitesOption,
    sectorsOption,
    eventManagersOption,
    participantsOptions,
    themesOptions,
    creatorsOptions,
    selectedClients,
    selectedSites,
    selectedSectors,
    selectedEventManagers,
    selectedParticipants,
    selectedThemes,
    selectedCreators,
    setSelectedClients,
    setSelectedSites,
    setSelectedSectors,
    setSelectedEventManagers,
    setSelectedParticipants,
    setSelectedThemes,
    setSelectedCreators,
    creationDate,
    deliveryDate,
    archivedDate,
    completionDate,
    setCreationDateFunct,
    setDeliveryDateFunct,
    setArchivedDateFunct,
    setCompletionDateFunct,
  } = props;

  return (
    <div className={`${FILTER_WIDTH} flex flex-col gap-4`}>
      <SavedViewsWithFilters
        buildCurrentFiltersAndColumns={buildCurrentFiltersAndColumns}
        applySavedView={applySavedView}
        selectedSavedViewId={selectedSavedViewId}
        onChangeSelectedViewId={onChangeSelectedViewId}
        onClearFilters={onClearFilters}
      />

      <FilterPanel>
        <ExtractionFiltersPanel
          filterWidthClass={FILTER_WIDTH}
          clientsOption={clientsOption}
          sitesOption={sitesOption}
          sectorsOption={sectorsOption}
          eventManagersOption={eventManagersOption}
          participantsOptions={participantsOptions}
          themesOptions={themesOptions}
          creatorsOptions={creatorsOptions}
          selectedClients={selectedClients}
          selectedSites={selectedSites}
          selectedSectors={selectedSectors}
          selectedEventManagers={selectedEventManagers}
          selectedParticipants={selectedParticipants}
          selectedThemes={selectedThemes}
          selectedCreators={selectedCreators}
          setSelectedClients={setSelectedClients}
          setSelectedSites={setSelectedSites}
          setSelectedSectors={setSelectedSectors}
          setSelectedEventManagers={setSelectedEventManagers}
          setSelectedParticipants={setSelectedParticipants}
          setSelectedThemes={setSelectedThemes}
          setSelectedCreators={setSelectedCreators}
          creationDate={creationDate}
          deliveryDate={deliveryDate}
          archivedDate={archivedDate}
          completionDate={completionDate}
          setCreationDateFunct={setCreationDateFunct}
          setDeliveryDateFunct={setDeliveryDateFunct}
          setArchivedDateFunct={setArchivedDateFunct}
          setCompletionDateFunct={setCompletionDateFunct}
        />
      </FilterPanel>
    </div>
  );
};

function SavedViewsWithFilters( {
  buildCurrentFiltersAndColumns,
  applySavedView,
  selectedSavedViewId,
  onChangeSelectedViewId,
  onClearFilters,
}: SavedViewsWithFiltersProps ): JSX.Element | null {
  const { isCollapsed } = useFilter();
  if ( isCollapsed ) {
    return null;
  }

  return (
    <div className={FILTER_WIDTH}>
      <SavedViewsManager
        orientation="vertical"
        dataType="filters"
        getCurrentFiltersAndColumns={buildCurrentFiltersAndColumns}
        onApplyView={applySavedView}
        selectedViewId={selectedSavedViewId}
        onChangeSelectedViewId={onChangeSelectedViewId}
        onClearSelection={onClearFilters}
      />
    </div>
  );
}

function ExtractionView(): JSX.Element {

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const user = useSelector( ( state: RootState ) => { return state.user.value; } );

  const [ popupExtractVoidFile, setPopupExtractVoidFile ] = useState<boolean>( false );

  const [ selectedClients, setSelectedClients ] = useState<Option<Client>[]>( [] );
  const [ clientsOption, setClientsOption ] = useState<Option<Client>[]>( [] );
  const [ allClients, setAllClients ] = useState<Client[]>( [] );

  const [ selectedSites, setSelectedSites ] = useState<Option<Site>[]>( [] );
  const [ sitesOption, setSitesOption ] = useState<GroupedOption<Site>[]>( [] );
  const [ allSites, setAllSites ] = useState<Site[]>( [] );

  const [ selectedSectors, setSelectedSectors ] = useState<Option<Sector>[]>( [] );
  const [ sectorsOption, setSectorsOption ] = useState<GroupedOption<Sector>[]>( [] );
  const [ allSectors, setAllSectors ] = useState<Sector[]>( [] );

  const [ selectedEventManagers, setSelectedEventManagers ] = useState<Option<AdminTableWithSitesApiResponse>[]>( [] );
  const [ eventManagersOption, setEventManagersOption ] = useState<GroupedOption<AdminTableWithSitesApiResponse>[]>( [] );
  const [ allEventManagers, setAllEventManagers ] = useState<AdminTableWithSitesApiResponse[]>( [] );

  const [ selectedParticipants, setSelectedParticipants ] = useState<Option<Participant>[]>( [] );
  const [ selectedCreators, setSelectedCreators ] = useState<Option<Participant>[]>( [] );

  const [ selectedThemes, setSelectedThemes ] = useState<Option<ApiTheme>[]>( [] );
  const [ themesOptions, setThemesOptions ] = useState<GroupedOption<ApiTheme>[]>( [] );
  const [ allThemes, setAllThemes ] = useState<Theme[]>( [] );

  const [ hasMultipleSheet, setMultipleSheet ] = useState<string>( 'multi_sheets' );
  const [ multiSheetGrouping, setMultiSheetGrouping ] = useState<'client' | 'site' | 'sector' | 'table'>( 'client' );

  const [ creationDate, setCreationDate ] = useState<Date[]>( [] );

  const [ deliveryDate, setDeliveryDate ] = useState<Date[]>( [] );

  const [ completionDate, setCompletionDate ] = useState<Date[]>( [] );

  const [ archivedDate, setArchivedDate ] = useState<Date[]>( [] );

  const [ renderList, setRenderList ] = useState<number>( 1 );
  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );

  const [ isEngagementModalOpen, setIsEngagementModalOpen ] = useState<boolean>( false );
  const [ selectedEventCommitment, setSelectedEventCommitment ] = useState<EventCommitment | undefined>( undefined );
  const [ selectedSavedViewId, setSelectedSavedViewId ] = useState<string | null>( null );
  const [ selectedColumnsViewId, setSelectedColumnsViewId ] = useState<string | null>( null );
  const [ selectedColumns, setSelectedColumns ] = useState<EventTableColumnKey[]>( EVENTS_TABLE_COLUMNS_META.map( column => { return column.key; } ) );

  const [ pendingSiteIds, setPendingSiteIds ] = useState<string[]>( [] );
  const [ pendingSectorIds, setPendingSectorIds ] = useState<string[]>( [] );
  const [ pendingEventManagerIds, setPendingEventManagerIds ] = useState<string[]>( [] );
  const [ pendingThemeIds, setPendingThemeIds ] = useState<string[]>( [] );
  const [ pendingParticipantIds, setPendingParticipantIds ] = useState<string[]>( [] );
  const [ pendingCreatorId, setPendingCreatorId ] = useState<string | null>( null );

  // Cache local pour éviter de rappeler /users avec les mêmes filtres
  const participantsCacheRef = useRef<Map<string, ListPaginationResult<Participant>>>( new Map() );
  // Cache local pour éviter de rappeler les APIs sites / secteurs / thèmes avec les mêmes filtres
  const sitesByClientsCacheRef = useRef<Map<string, Site[]>>( new Map() );
  const sectorsBySitesCacheRef = useRef<Map<string, Sector[]>>( new Map() );
  const usersByEventManagersCacheRef = useRef<Map<string, Participant[]>>( new Map() );
  const themesByFilterCacheRef = useRef<Map<string, Theme[]>>( new Map() );
  const filtersSignatureRef = useRef<string | null>( null );

  useEffect( () => {
    void initExtractionView( {
      token,
      dispatch,
      user,
      t,
      setClientsOption,
      setAllClients,
      setAllSites,
      setSitesOption,
      setSelectedSites,
      setAllSectors,
      setSectorsOption,
      setSelectedSectors,
      setAllEventManagers,
      setEventManagersOption,
      setSelectedEventManagers,
      setAllThemes,
      setIsFirstRender,
      selectedClients,
      selectedSites,
      selectedSectors,
      allSites,
      allSectors,
      multiSheetGrouping,
    } );
  }, [] );

  useEffect( () => {
    const clientsId = selectedClients.map( client => { return client.object.id; } );
    if ( !isFirstRender && clientsId.length > 0 ) {
      const fetchSites = async () => {
        let updatedSiteOptions: Option<Site>[] = [];
        if ( clientsId.length > 0 && clientsId.every( id => { return GlobalService.isValidGuid( id ); } ) ) {
          const cacheKey = JSON.stringify( [ ...clientsId ].sort() );
          let sites: Site[] | undefined = sitesByClientsCacheRef.current.get( cacheKey );
          if ( !sites ) {
            sites = await CommonSiteService.getAllowedSitesByClientsId( clientsId, token, dispatch );
            sitesByClientsCacheRef.current.set( cacheKey, sites );
          }
          updatedSiteOptions = CommonGlobalService.toOptions( sites );
        }
        const groupedSites: GroupedOption<Site>[] = [];
        for ( const [ clientId, options ] of Object.entries( CommonGlobalService.groupBy( updatedSiteOptions, option => { return option.object.clientId; } ) ) ) {
          const optionSites = options ?? [];
          CommonUtilsService.sortByKey( optionSites, 'label', 'asc' );
          groupedSites.push( {
            label: selectedClients.find( client => { return client.object.id === clientId; } )?.object.name ?? t( 'common:error.missing_client' ),
            options: optionSites
          } );
        }
        setSitesOption( groupedSites );
      };
      fetchSites();
    } else if ( !isFirstRender && clientsId.length === 0 ) {
      const allSitesOptions = CommonGlobalService.toOptions( allSites );
      const groupedSites: GroupedOption<Site>[] = [];
      for ( const [ clientId, options ] of Object.entries( CommonGlobalService.groupBy( allSitesOptions, option => { return option.object.clientId; } ) ) ) {
        const optionSites = options ?? [];
        CommonUtilsService.sortByKey( optionSites, 'label', 'asc' );
        groupedSites.push( {
          label: allClients.find( client => { return client.id === clientId; } )?.name ?? t( 'common:error.missing_client' ),
          options: optionSites
        } );
      }
      setSitesOption( groupedSites );
    }
  }, [ selectedClients, allSites, allClients ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const sites = sitesOption
        .map( site => { return site.options; } )
        .flat();
      const filteredSelectedSites = selectedSites.filter( selectedSite => {
        return sites.some( site => {
          return site.object.id === selectedSite.object.id;
        } );
      } );
      setSelectedSites( filteredSelectedSites );
    }
  }, [ sitesOption ] );

  useEffect( () => {
    if ( !isFirstRender && pendingSiteIds.length > 0 && sitesOption.length > 0 ) {
      const allSitesOptions = sitesOption
        .map( site => { return site.options; } )
        .flat();
      const newSelectedSites = allSitesOptions.filter( option => { return pendingSiteIds.includes( option.object.id ); } );
      if ( newSelectedSites.length === pendingSiteIds.length ) {
        setSelectedSites( newSelectedSites );
        setPendingSiteIds( [] );
      }
    }
  }, [ pendingSiteIds, sitesOption, isFirstRender ] );
  useEffect( () => {
    const sitesId = selectedSites.map( client => { return client.object.id; } );
    if ( !isFirstRender && sitesId.length > 0 ) {
      const fetchSectors = async () => {
        let updatedSectorOptions: Option<Sector>[] = [];
        if ( sitesId.length > 0 && sitesId.every( id => { return GlobalService.isValidGuid( id ); } ) ) {
          const cacheKey = JSON.stringify( [ ...sitesId ].sort() );
          let sectors: Sector[] | undefined = sectorsBySitesCacheRef.current.get( cacheKey );
          if ( !sectors ) {
            sectors = await CommonSectorService.getAllowedSectorsBySitesId( sitesId, token, dispatch );
            sectorsBySitesCacheRef.current.set( cacheKey, sectors );
          }
          updatedSectorOptions = CommonGlobalService.toOptions( sectors );
        }
        const groupedSectors: GroupedOption<Sector>[] = [];
        for ( const [ siteId, options ] of Object.entries( CommonGlobalService.groupBy( updatedSectorOptions, option => { return option.object.siteId; } ) ) ) {
          const optionSectors = options ?? [];
          CommonUtilsService.sortByKey( optionSectors, 'label', 'asc' );
          groupedSectors.push( {
            label: selectedSites.find( site => { return site.object.id === siteId; } )?.object.name ?? t( 'common:error.missing_site' ),
            options: optionSectors
          } );
        }
        setSectorsOption( groupedSectors );
      };
      fetchSectors();
    } else if ( !isFirstRender && sitesId.length === 0 ) {
      const groupedSectors: GroupedOption<Sector>[] = [];
      const allSectorsOptions = CommonGlobalService.toOptions( allSectors );

      // Filtrer les secteurs en fonction des clients sélectionnés (si aucun site n'est sélectionné)
      const allowedSiteIds = selectedClients.length > 0
        ? allSites
          .filter( site => { return selectedClients.some( client => { return client.object.id === site.clientId; } ); } )
          .map( site => { return site.id; } )
        : allSites.map( site => { return site.id; } );

      const filteredSectorsOptions = allSectorsOptions.filter( option => {
        return allowedSiteIds.includes( option.object.siteId );
      } );

      for ( const [ siteId, options ] of Object.entries( CommonGlobalService.groupBy( filteredSectorsOptions, option => { return option.object.siteId; } ) ) ) {
        const optionSectors = options ?? [];
        CommonUtilsService.sortByKey( optionSectors, 'label', 'asc' );
        groupedSectors.push( {
          label: allSites.find( site => { return site.id === siteId; } )?.name ?? t( 'common:error.missing_site' ),
          options: optionSectors
        } );
      }
      setSectorsOption( groupedSectors );
    }
  }, [ selectedSites, selectedClients ] );

  useEffect( () => {
    const fetchPendingParticipantsAndCreator = async () => {
      if ( pendingParticipantIds.length === 0 && !pendingCreatorId ) {
        return;
      }

      const idsToFetch = Array.from( new Set( [
        ...pendingParticipantIds,
        ...( pendingCreatorId ? [ pendingCreatorId ] : [] ),
      ] ) );

      const users: unknown[] = [];

      for ( const id of idsToFetch ) {
        if ( GlobalService.isValidGuid( id ) ) {
          try {
            const fetchedUser = await UsersService.getUser( id as UUID );
            users.push( fetchedUser );
          } catch ( error ) {
            logger.warn( 'Failed to load user for pending participant/creator', { id, error } );
          }
        }
      }

      const needsBacklogParticipant = pendingParticipantIds.includes( DEFAULT_BACKLOG.ID );
      const needsBacklogCreator = pendingCreatorId === DEFAULT_BACKLOG.ID;

      if ( users.length === 0 && !needsBacklogParticipant && !needsBacklogCreator ) {
        setPendingParticipantIds( [] );
        setPendingCreatorId( null );
        return;
      }

      if ( needsBacklogParticipant || needsBacklogCreator ) {
        const backlogUser = {
          id: DEFAULT_BACKLOG.ID,
          fullname: ( ( window as unknown as { i18next?: { t?: ( key: string ) => string } } ).i18next?.t?.( DEFAULT_BACKLOG.NAME ) ) ?? DEFAULT_BACKLOG.NAME,
        };
        users.push( backlogUser );
      }

      const options = CommonGlobalService.toOptions( users.map( userItem => {
        const anyUser = userItem as unknown as { id: string; fullname?: string; firstname?: string; lastname?: string };
        const fullname = anyUser.fullname ?? `${anyUser.firstname ?? ''} ${anyUser.lastname ?? ''}`.trim();
        const [ firstname, ...rest ] = fullname.split( ' ' );
        const lastname = rest.join( ' ' );
        const participant: Participant = {
          id: anyUser.id,
          eventManagerId: '',
          firstname,
          lastname,
          position: 0,
          isAdmin: false,
        };
        return { ...participant, fullname };
      } ) );

      if ( pendingParticipantIds.length > 0 ) {
        const newSelectedParticipants = options.filter( option => { return pendingParticipantIds.includes( option.object.id ); } );
        if ( newSelectedParticipants.length > 0 ) {
          setSelectedParticipants( newSelectedParticipants );
        }
      }

      if ( pendingCreatorId ) {
        const creatorOption = options.find( option => { return option.object.id === pendingCreatorId; } );
        if ( creatorOption ) {
          setSelectedCreators( [ creatorOption ] );
        }
      }

      setPendingParticipantIds( [] );
      setPendingCreatorId( null );
    };

    void fetchPendingParticipantsAndCreator();
  }, [ pendingParticipantIds, pendingCreatorId, token, dispatch ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const sectors = sectorsOption
        .map( sector => { return sector.options; } )
        .flat();
      const filteredSelectedSectors = selectedSectors.filter( selectedSector => {
        return sectors.some( sector => {
          return sector.object.id === selectedSector.object.id;
        } );
      } );
      setSelectedSectors( filteredSelectedSectors );
    }
  }, [ sectorsOption ] );

  useEffect( () => {
    if ( !isFirstRender && pendingSectorIds.length > 0 && sectorsOption.length > 0 ) {
      const allSectorsOptions = sectorsOption
        .map( sector => { return sector.options; } )
        .flat();
      const newSelectedSectors = allSectorsOptions.filter( option => { return pendingSectorIds.includes( option.object.id ); } );
      if ( newSelectedSectors.length === pendingSectorIds.length ) {
        setSelectedSectors( newSelectedSectors );
        setPendingSectorIds( [] );
      }
    }
  }, [ pendingSectorIds, sectorsOption, isFirstRender ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const eventManagers = eventManagersOption
        .map( eventManager => { return eventManager.options; } )
        .flat();
      const filteredSelectedEventManagers = selectedEventManagers.filter( selectedEventManager => {
        return eventManagers.some( eventManager => {
          return eventManager.object.id === selectedEventManager.object.id;
        } );
      } );
      setSelectedEventManagers( filteredSelectedEventManagers );
    }
  }, [ eventManagersOption ] );

  useEffect( () => {
    if ( !isFirstRender && allEventManagers.length > 0 ) {
      const groupedEventManagers = buildEventManagersOptions( allEventManagers, selectedClients, selectedSites, selectedSectors, allSites, allSectors, multiSheetGrouping, t );
      setEventManagersOption( groupedEventManagers );
    }
  }, [ selectedClients, selectedSites, selectedSectors, allEventManagers, allSites, allSectors, multiSheetGrouping, isFirstRender ] );

  useEffect( () => {
    if ( !isFirstRender && pendingEventManagerIds.length > 0 && eventManagersOption.length > 0 ) {
      const allEventManagersOptions = eventManagersOption
        .map( eventManager => { return eventManager.options; } )
        .flat();
      const newSelectedEventManagers = allEventManagersOptions.filter( option => { return pendingEventManagerIds.includes( option.object.id ); } );
      if ( newSelectedEventManagers.length === pendingEventManagerIds.length ) {
        setSelectedEventManagers( newSelectedEventManagers );
        setPendingEventManagerIds( [] );
      }
    }
  }, [ pendingEventManagerIds, eventManagersOption, isFirstRender ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const themes = themesOptions
        .map( theme => { return theme.options; } )
        .flat();
      const filteredSelectedThemes = selectedThemes.filter( selectedTheme => {
        return themes.some( theme => {
          return theme.object.id === selectedTheme.object.id;
        } );
      } );
      setSelectedThemes( filteredSelectedThemes );
    }
  }, [ themesOptions ] );

  useEffect( () => {
    if ( isFirstRender ) {
      return;
    }

    const refreshThemes = async () => {
      const clientIds = selectedClients.map( client => { return client.object.id; } );
      const siteIds = selectedSites.map( site => { return site.object.id; } );
      const sectorIds = selectedSectors.map( sector => { return sector.object.id; } );
      const eventManagerIds = selectedEventManagers.map( em => { return em.object.id; } );

      try {
        const cacheKey = JSON.stringify( {
          clientIds: [ ...clientIds ].sort(),
          siteIds: [ ...siteIds ].sort(),
          sectorIds: [ ...sectorIds ].sort(),
          eventManagerIds: [ ...eventManagerIds ].sort(),
        } );

        let themes = themesByFilterCacheRef.current.get( cacheKey );
        if ( !themes ) {
          themes = await ThemeService.getThemesForExtractionFilters( {
            clientIds,
            siteIds,
            sectorIds,
            eventManagerIds,
          }, token, dispatch );
          themesByFilterCacheRef.current.set( cacheKey, themes );
        }

        if ( themes.length > 0 ) {
          setThemesOptions( buildThemesOptions( themes, allSites, t ) );
        } else {
          setThemesOptions( [] );
        }
      } catch ( error ) {
        logger.warn( 'Failed to load themes for extraction filters', { error } );
        setThemesOptions( [] );
      }
    };

    void refreshThemes();
  }, [ selectedClients, selectedSites, selectedSectors, selectedEventManagers, allThemes, allSites, isFirstRender ] );

  useEffect( () => {
    if ( !isFirstRender && pendingThemeIds.length > 0 && themesOptions.length > 0 ) {
      const allThemesOptions = themesOptions
        .map( theme => { return theme.options; } )
        .flat();
      const newSelectedThemes = allThemesOptions.filter( option => { return pendingThemeIds.includes( option.object.id ); } );
      if ( newSelectedThemes.length === pendingThemeIds.length ) {
        setSelectedThemes( newSelectedThemes );
        setPendingThemeIds( [] );
      }
    }
  }, [ pendingThemeIds, themesOptions, isFirstRender ] );

  useEffect( () => {
    if ( isFirstRender ) {
      return;
    }

    const signatureObject = {
      clientIds: selectedClients.map( c => { return c.object.id; } ).sort(),
      siteIds: selectedSites.map( s => { return s.object.id; } ).sort(),
      sectorIds: selectedSectors.map( s => { return s.object.id; } ).sort(),
      eventManagerIds: selectedEventManagers.map( em => { return em.object.id; } ).sort(),
      participantIds: selectedParticipants.map( p => { return p.object.id; } ).sort(),
      creatorIds: selectedCreators.map( c => { return c.object.id; } ).sort(),
      themeIds: selectedThemes.map( th => { return th.object.id; } ).sort(),
      createdDate: creationDate.map( d => { return dayjs( d ).format( FORMAT_DATE_API ); } ),
      deliveryDate: deliveryDate.map( d => { return dayjs( d ).format( FORMAT_DATE_API ); } ),
      archivedDate: archivedDate.map( d => { return dayjs( d ).format( FORMAT_DATE_API ); } ),
      completionDate: completionDate.map( d => { return dayjs( d ).format( FORMAT_DATE_API ); } ),
    };

    const newSignature = JSON.stringify( signatureObject );

    if ( filtersSignatureRef.current !== newSignature ) {
      filtersSignatureRef.current = newSignature;
      setRenderList( prev => { return prev + 1; } );
    }
  }, [ isFirstRender, selectedClients, selectedSites, selectedSectors, selectedEventManagers, selectedParticipants, selectedCreators, selectedThemes, archivedDate, completionDate, creationDate, deliveryDate ] );

  const getParticipantsOptions = async ( filter: FilterPagination ): Promise<ListPaginationResult<Participant>> => {
    // Si des tableaux sont sélectionnés, tirer les participants par tableau
    if ( selectedEventManagers.length > 0 ) {
      const eventManagerIds = selectedEventManagers.map( em => { return em.object.id; } );
      const cacheKey = JSON.stringify( [ ...eventManagerIds ].sort() );
      let users = usersByEventManagersCacheRef.current.get( cacheKey );
      if ( !users ) {
        let inFlight = usersByEventManagersInFlightCache.get( cacheKey );
        if ( !inFlight ) {
          inFlight = ( async () => {
            const usersRaw = await EventManagerService.getUsersByEventManagerIds( eventManagerIds, token, dispatch );
            const uniqueById = new Map<string, unknown>();
            for ( const userItem of usersRaw ) {
              const anyUser = userItem as unknown as { id: string };
              if ( !uniqueById.has( anyUser.id ) ) {
                uniqueById.set( anyUser.id, userItem );
              }
            }

            const dedupedUsers = Array.from( uniqueById.values() );

            const mappedUsers = dedupedUsers.map( userItem => {
              const anyUser = userItem as unknown as { id: string; firstname?: string; lastname?: string; fullname?: string };
              const firstname = anyUser.firstname ?? '';
              const lastname = anyUser.lastname ?? '';
              const participant: Participant = {
                id: anyUser.id,
                eventManagerId: '',
                firstname,
                lastname,
                position: 0,
                isAdmin: false,
              };
              return participant;
            } );
            usersByEventManagersCacheRef.current.set( cacheKey, mappedUsers );
            usersByEventManagersInFlightCache.delete( cacheKey );
            return mappedUsers;
          } )();
          usersByEventManagersInFlightCache.set( cacheKey, inFlight );
        }
        users = await inFlight;
      }

      const options = CommonGlobalService.toOptions( users.map( userItem => {
        const fullname = `${userItem.firstname ?? ''} ${userItem.lastname ?? ''}`.trim();
        return { ...userItem, fullname };
      } ) );

      return {
        total: options.length,
        list: options,
      };
    }

    // Sinon, filtrer par client / site / secteur comme avant
    const userFilter: UserFilter = {
      page: filter.page,
      step: filter.step,
      filterName: filter.filterName,
      filterClients: selectedClients.length > 0 ? selectedClients.map( client => { return client.object.id; } ) : undefined,
      filterSites: selectedSites.length > 0 ? selectedSites.map( site => { return site.object.id; } ) : undefined,
      filterSectors: selectedSectors.length > 0 ? selectedSectors.map( sector => { return sector.object.id; } ) : undefined,
    };

    const cacheKey = JSON.stringify( userFilter );
    const cached = participantsCacheRef.current.get( cacheKey );
    if ( cached ) {
      return cached;
    }

    let inFlight = participantsInFlightCache.get( cacheKey );
    if ( !inFlight ) {
      inFlight = ( async () => {
        const usersResponse = await UsersService.getAllowedUsers( userFilter );
        const users = usersResponse.users ?? [];

        const options = CommonGlobalService.toOptions( users.map( userItem => {
          const anyUser = userItem as unknown as { fullname?: string; firstname?: string; lastname?: string };
          const fullname = anyUser.fullname ?? `${anyUser.firstname ?? ''} ${anyUser.lastname ?? ''}`.trim();
          const [ firstname, ...rest ] = fullname.split( ' ' );
          const lastname = rest.join( ' ' );
          const participant: Participant = {
            id: userItem.id,
            eventManagerId: '',
            firstname,
            lastname,
            position: 0,
            isAdmin: false,
          };
          return { ...participant, fullname };
        } ) );
        const result: ListPaginationResult<Participant> = {
          total: usersResponse.total ?? options.length,
          list: options,
        };

        participantsCacheRef.current.set( cacheKey, result );
        participantsInFlightCache.delete( cacheKey );

        return result;
      } )();
      participantsInFlightCache.set( cacheKey, inFlight );
    }

    return await inFlight;
  };

  const fetchJsonData = async ( page: number, step: number, { key, order }: { key: string, order: ( 'asc' | 'desc' ) } ) => {
    const filters: DataExportFilters = {
      page,
      step,
      order: { key, order },
      multipleSheets: hasMultipleSheet === 'multi_sheets',
      ...( selectedClients.length > 0 && { clientIds: selectedClients.map( client => { return client.object.id; } ) } ),
      ...( selectedSites.length > 0 && { siteIds: selectedSites.map( client => { return client.object.id; } ) } ),
      ...( selectedSectors.length > 0 && { sectorIds: selectedSectors.map( client => { return client.object.id; } ) } ),
      ...( selectedEventManagers.length > 0 && { eventManagerIds: selectedEventManagers.map( client => { return client.object.id; } ) } ),
      ...( creationDate.length === 2 && {
        createdDateRange: {
          start: dayjs( creationDate[ 0 ] ).format( FORMAT_DATE_API ),
          end: dayjs( creationDate[ 1 ] ).format( FORMAT_DATE_API ),
        },
      } ),
      ...( selectedParticipants.length > 0 && { participantIds: selectedParticipants.map( client => { return client.object.id; } ) } ),
      ...( deliveryDate.length === 2 && {
        deliveryDateRange: {
          start: dayjs( deliveryDate[ 0 ] ).format( FORMAT_DATE_API ),
          end: dayjs( deliveryDate[ 1 ] ).format( FORMAT_DATE_API ),
        },
      } ),
      ...( selectedThemes.length > 0 && { themeIds: selectedThemes.map( client => { return client.object.id; } ) } ),
      ...( completionDate.length === 2 && {
        completionDateRange: {
          start: dayjs( completionDate[ 0 ] ).format( FORMAT_DATE_API ),
          end: dayjs( completionDate[ 1 ] ).format( FORMAT_DATE_API ),
        },
      } ),
      ...( archivedDate.length === 2 && {
        archivedDateRange: {
          start: dayjs( archivedDate[ 0 ] ).format( FORMAT_DATE_API ),
          end: dayjs( archivedDate[ 1 ] ).format( FORMAT_DATE_API ),
        },
      } ),
      ...( selectedCreators.length > 0 && { createdBy: selectedCreators.map( client => { return client.object.id; } )[ 0 ] } ),
      ...( archivedDate.length === 2 && { archived: true } ),
      ...( completionDate.length === 2 && { completed: true } ),
    };

    const result = await DataExportService.getDataExportAsJSON( filters, token, dispatch );
    return result;
  };

  const applySavedFilters = ( savedFilters: ExtractionSavedViewFilter[] ) => {
    applySavedFiltersHelper( savedFilters, {
      clientsOption,
      setSelectedClients,
      setCreationDate,
      setDeliveryDate,
      setArchivedDate,
      setCompletionDate,
      setMultipleSheet,
      setPendingSiteIds,
      setPendingSectorIds,
      setPendingEventManagerIds,
      setPendingParticipantIds,
      setPendingThemeIds,
      setPendingCreatorId,
    } );
  };

  const applySavedView = ( view: ExtractionSavedView ) => {
    if ( view.filters && view.filters.length > 0 ) {
      resetAllFiltersAndSelection();
      applySavedFilters( view.filters );
      setSelectedSavedViewId( String( view.id ) );
      return;
    }

    if ( view.columns && view.columns.length > 0 ) {
      const allowedKeys = new Set( EVENTS_TABLE_COLUMNS_META.map( column => { return column.key; } ) );
      const nextColumns = view.columns
        .map( column => { return column.key as EventTableColumnKey; } )
        .filter( key => { return allowedKeys.has( key ); } );

      setSelectedColumns( nextColumns.length > 0 ? nextColumns : EVENTS_TABLE_COLUMNS_META.map( column => { return column.key; } ) );
      setSelectedColumnsViewId( String( view.id ) );
    }
  };

  const resetAllFiltersAndSelection = () => {
    setSelectedClients( [] );
    setSelectedSites( [] );
    setSelectedSectors( [] );
    setSelectedEventManagers( [] );
    setSelectedParticipants( [] );
    setSelectedThemes( [] );
    setSelectedCreators( [] );
    setCreationDate( [] );
    setDeliveryDate( [] );
    setArchivedDate( [] );
    setCompletionDate( [] );
    setMultipleSheet( 'multi_sheets' );
    setMultiSheetGrouping( 'client' );
    setSelectedSavedViewId( null );
  };

  const buildCurrentFiltersAndColumns = (): { filters: ExtractionSavedViewFilterInput[]; columns: ExtractionSavedViewColumnInput[] } => {
    return buildCurrentFiltersAndColumnsHelper( {
      selectedClients,
      selectedSites,
      selectedSectors,
      selectedEventManagers,
      selectedParticipants,
      selectedThemes,
      selectedCreators,
      creationDate,
      deliveryDate,
      archivedDate,
      completionDate,
      hasMultipleSheet,
      selectedColumns,
    } );
  };

  //----------- SET THE DATES ---------------\
  const setCreationDateFunct = ( startDate: Date | null, endDate: Date | null ) => {
    const updatedValue = [];
    if ( startDate != null && endDate != null ) {
      updatedValue.push( startDate, endDate );
    }
    setCreationDate( updatedValue );
  };

  const setCompletionDateFunct = ( startDate: Date | null, endDate: Date | null ) => {
    const updatedValue = [];
    if ( startDate != null && endDate != null ) {
      updatedValue.push( startDate, endDate );
    }
    setCompletionDate( updatedValue );
  };

  const setDeliveryDateFunct = ( startDate: Date | null, endDate: Date | null ) => {
    const updatedValue = [];
    if ( startDate != null && endDate != null ) {
      updatedValue.push( startDate, endDate );
    }
    setDeliveryDate( updatedValue );
  };

  const setArchivedDateFunct = ( startDate: Date | null, endDate: Date | null ) => {
    const updatedValue = [];
    if ( startDate != null && endDate != null ) {
      updatedValue.push( startDate, endDate );
    }
    setArchivedDate( updatedValue );
  };

  //----------- EXPORT ---------------\\
  const handleExport = async () => {
    const filters: DataExportFilters = {
      multipleSheets: hasMultipleSheet === 'multi_sheets',
      ...( hasMultipleSheet === 'multi_sheets' && { multisheet: multiSheetGrouping } ),
      ...( selectedClients.length > 0 && { clientIds: selectedClients.map( client => { return client.object.id; } ) } ),
      ...( selectedSites.length > 0 && { siteIds: selectedSites.map( client => { return client.object.id; } ) } ),
      ...( selectedSectors.length > 0 && { sectorIds: selectedSectors.map( client => { return client.object.id; } ) } ),
      ...( selectedEventManagers.length > 0 && { eventManagerIds: selectedEventManagers.map( client => { return client.object.id; } ) } ),
      ...( creationDate.length === 2 && { createdDateRange: { start: dayjs( creationDate[ 0 ] ).format( FORMAT_DATE_API ), end: dayjs( creationDate[ 1 ] ).format( FORMAT_DATE_API ) } } ),
      ...( selectedParticipants.length > 0 && { participantIds: selectedParticipants.map( client => { return client.object.id; } ) } ),
      ...( deliveryDate.length === 2 && { deliveryDateRange: { start: dayjs( deliveryDate[ 0 ] ).format( FORMAT_DATE_API ), end: dayjs( deliveryDate[ 1 ] ).format( FORMAT_DATE_API ) } } ),
      ...( selectedThemes.length > 0 && { themeIds: selectedThemes.map( client => { return client.object.id; } ) } ),
      ...( completionDate.length === 2 && { completionDateRange: { start: dayjs( completionDate[ 0 ] ).format( FORMAT_DATE_API ), end: dayjs( completionDate[ 1 ] ).format( FORMAT_DATE_API ) } } ),
      ...( archivedDate.length === 2 && { archivedDateRange: { start: dayjs( archivedDate[ 0 ] ).format( FORMAT_DATE_API ), end: dayjs( archivedDate[ 1 ] ).format( FORMAT_DATE_API ) } } ),
      ...( selectedCreators.length > 0 && { createdBy: selectedCreators.map( client => { return client.object.id; } )[ 0 ] } ),
      ...( ( archivedDate && archivedDate.length > 0 ) && { archived: true } ),
      ...( ( completionDate && completionDate.length > 0 ) && { completed: true } ),
    };

    const data = await DataExportService.getDataExport( filters, token, dispatch );
    if ( data == null ) {
      setPopupExtractVoidFile( true );
    }
  };

  const fetchNumberOfSheets = () => {
    return Promise.resolve( [
      {
        options: [
          { id: 'multi_sheets', label: `${t( 'translation:multi_sheets' )}` },
          { id: 'one_sheet', label: `${t( 'translation:one_sheet' )}` },
        ],
      },
    ] );
  };

  const fetchMultiSheetGroupingOptions = () => {
    return Promise.resolve( [
      {
        options: [
          { id: 'client', label: t( 'common:client' ) },
          { id: 'site', label: t( 'common:site' ) },
          { id: 'sector', label: t( 'common:sector' ) },
          { id: 'table', label: t( 'common:table' ) },
        ],
      },
    ] );
  };

  //----------- HANDLE ROW CLICK ---------------\\
  const handleRowClick = ( item: EventExported ) => {
    const eventManagerIds = Array.isArray( item.tableIds ) && item.tableIds.length > 0
      ? item.tableIds
      : [ item.table_id ];

    const eventCommitment: EventCommitment = {
      id: item.id,
      eventId: item.id,
      eventTitle: item.title || '',
      participants: [],
      eventManagerIds,
      deliveryDate: item.deliveryDate || '',
      completed: item.status === 'Terminé' || item.completedDate !== '',
      completedDate: item.completedDate || '',
      archived: item.status === 'Archivé' || item.archivedDate !== '',
      color: item.color || '#3B82F6',
      isEventRecurring: item.recurringEvent === 'true' ? 1 : 0,
      haveNote: !!item.note,
      haveDocument: false,
      numberOfLateDays: 0,
      complatedPercentage: 0,
      highlighted: item.highlightedEvent ? 1 : 0,
    };

    setSelectedEventCommitment( eventCommitment );
    setIsEngagementModalOpen( true );
  };

  /*---------------------------------- Front ----------------------------------*/
  return (
    <div className="overflow-x-hidden">
      <Page title={t( 'common:extraction' )}>
        {
          isFirstRender ?
            <Loading size={ 100 }/>
            :
            <DataChecker clients={allClients} sites={allSites} sectors={allSectors}>
              <FilterProvider>
                <div className="w-full p-8 mb-8 justify-self-center overflow-x-hidden">

                  {/* Barre supérieure : Affiner à gauche, export à droite */}
                  <div className="w-full flex flex-col lg:flex-row justify-between items-start mb-6 gap-4">
                    <FilterButton className={FILTER_WIDTH} icon={FilterIcon}></FilterButton>

                    <div className="flex flex-col lg:flex-row gap-4 lg:items-start lg:justify-end flex-1">
                      <div className="flex flex-col md:flex-row gap-3 md:gap-5 w-full lg:w-auto justify-end">
                        <SelectInput
                          textSize='w-full'
                          selected={hasMultipleSheet}
                          fetchOptions={fetchNumberOfSheets}
                          onSelect={setMultipleSheet}
                        />
                        {hasMultipleSheet === 'multi_sheets' && (
                          <SelectInput
                            textSize='w-full'
                            selected={multiSheetGrouping}
                            fetchOptions={fetchMultiSheetGroupingOptions}
                            onSelect={( value: string ) => { setMultiSheetGrouping( value as 'client' | 'site' | 'sector' | 'table' ); }}
                          />
                        )}
                        <SubmitButton
                          label={t( 'common:export_to_excel' )}
                          icon={<FileIcon />}
                          onClick={handleExport}
                          bgColor="bg-green-base"
                          textColor="text-white"
                          width="w-auto"
                        />
                      </div>

                      {popupExtractVoidFile && (
                        <div className="w-full lg:w-auto">
                          <AlertBox
                            color="blue"
                            icon={<HelpIcon />}
                            message={t( 'translation:empty_file' )}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="w-full flex flex-col lg:flex-row gap-6 items-start">

                    <LeftFiltersColumn
                      buildCurrentFiltersAndColumns={buildCurrentFiltersAndColumns}
                      applySavedView={applySavedView}
                      selectedSavedViewId={selectedSavedViewId}
                      onChangeSelectedViewId={setSelectedSavedViewId}
                      onClearFilters={resetAllFiltersAndSelection}
                      clientsOption={clientsOption}
                      sitesOption={sitesOption}
                      sectorsOption={sectorsOption}
                      eventManagersOption={eventManagersOption}
                      participantsOptions={getParticipantsOptions}
                      themesOptions={themesOptions}
                      creatorsOptions={getParticipantsOptions}
                      selectedClients={selectedClients}
                      selectedSites={selectedSites}
                      selectedSectors={selectedSectors}
                      selectedEventManagers={selectedEventManagers}
                      selectedParticipants={selectedParticipants}
                      selectedThemes={selectedThemes}
                      selectedCreators={selectedCreators}
                      setSelectedClients={setSelectedClients}
                      setSelectedSites={setSelectedSites}
                      setSelectedSectors={setSelectedSectors}
                      setSelectedEventManagers={setSelectedEventManagers}
                      setSelectedParticipants={setSelectedParticipants}
                      setSelectedThemes={setSelectedThemes}
                      setSelectedCreators={setSelectedCreators}
                      creationDate={creationDate}
                      deliveryDate={deliveryDate}
                      archivedDate={archivedDate}
                      completionDate={completionDate}
                      setCreationDateFunct={setCreationDateFunct}
                      setDeliveryDateFunct={setDeliveryDateFunct}
                      setArchivedDateFunct={setArchivedDateFunct}
                      setCompletionDateFunct={setCompletionDateFunct}
                    />

                    {/* Colonne droite : tableau de synthèse */}
                    <div className="flex-1 flex flex-col gap-4 overflow-hidden w-full">
                      <div className="flex-1 w-full min-w-0">
                        <EventsTable
                          fetchJsonData={fetchJsonData}
                          renderList={renderList}
                          onRowClick={handleRowClick}
                          buildCurrentFiltersAndColumns={buildCurrentFiltersAndColumns}
                          applySavedView={applySavedView}
                          selectedSavedViewId={selectedColumnsViewId}
                          onChangeSelectedViewId={setSelectedColumnsViewId}
                          onClearSelection={() => {
                            setSelectedColumns( EVENTS_TABLE_COLUMNS_META.map( column => { return column.key; } ) );
                          }}
                          visibleColumns={selectedColumns}
                          onChangeVisibleColumns={setSelectedColumns}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal d'engagement */}
                {isEngagementModalOpen && selectedEventCommitment && (
                  <EngagementModal
                    onClose={() => {
                      setIsEngagementModalOpen( false );
                      setSelectedEventCommitment( undefined );
                    }}
                    engagementClicked={selectedEventCommitment}
                    isViewMode={false}
                  />
                )}
              </FilterProvider>
            </DataChecker>
        }
      </Page>
    </div>
  );


};

function applySavedFiltersHelper(
  savedFilters: ExtractionSavedViewFilter[],
  {
    clientsOption,
    setSelectedClients,
    setCreationDate,
    setDeliveryDate,
    setArchivedDate,
    setCompletionDate,
    setMultipleSheet,
    setPendingSiteIds,
    setPendingSectorIds,
    setPendingEventManagerIds,
    setPendingParticipantIds,
    setPendingThemeIds,
    setPendingCreatorId,
  }: {
    clientsOption: Option<Client>[];
    setSelectedClients: React.Dispatch<React.SetStateAction<Option<Client>[]>>;
    setCreationDate: React.Dispatch<React.SetStateAction<Date[]>>;
    setDeliveryDate: React.Dispatch<React.SetStateAction<Date[]>>;
    setArchivedDate: React.Dispatch<React.SetStateAction<Date[]>>;
    setCompletionDate: React.Dispatch<React.SetStateAction<Date[]>>;
    setMultipleSheet: React.Dispatch<React.SetStateAction<string>>;
    setPendingSiteIds: React.Dispatch<React.SetStateAction<string[]>>;
    setPendingSectorIds: React.Dispatch<React.SetStateAction<string[]>>;
    setPendingEventManagerIds: React.Dispatch<React.SetStateAction<string[]>>;
    setPendingParticipantIds: React.Dispatch<React.SetStateAction<string[]>>;
    setPendingThemeIds: React.Dispatch<React.SetStateAction<string[]>>;
    setPendingCreatorId: React.Dispatch<React.SetStateAction<string | null>>;
  }
): void {
  const getValue = ( key: string ): string | undefined => {
    return savedFilters.find( filter => { return filter.key === key; } )?.value;
  };

  const parseIdsArray = ( key: string ): string[] => {
    const raw = getValue( key );
    if ( !raw ) {
      return [];
    }
    try {
      const parsed = JSON.parse( raw );
      return Array.isArray( parsed ) ? parsed : [];
    } catch {
      return [];
    }
  };

  const clientIds = parseIdsArray( 'clientIds' );
  if ( clientIds.length > 0 && clientsOption.length > 0 ) {
    const newSelectedClients = clientsOption.filter( option => { return clientIds.includes( option.object.id ); } );
    setSelectedClients( newSelectedClients );
  }

  const siteIds = parseIdsArray( 'siteIds' );
  if ( siteIds.length > 0 ) {
    setPendingSiteIds( siteIds );
  }

  const sectorIds = parseIdsArray( 'sectorIds' );
  if ( sectorIds.length > 0 ) {
    setPendingSectorIds( sectorIds );
  }

  const eventManagerIds = parseIdsArray( 'eventManagerIds' );
  if ( eventManagerIds.length > 0 ) {
    setPendingEventManagerIds( eventManagerIds );
  }

  const participantIds = parseIdsArray( 'participantIds' );
  if ( participantIds.length > 0 ) {
    setPendingParticipantIds( participantIds );
  }

  const themeIds = parseIdsArray( 'themeIds' );
  if ( themeIds.length > 0 ) {
    setPendingThemeIds( themeIds );
  }

  const createdBy = getValue( 'createdBy' );
  if ( createdBy ) {
    setPendingCreatorId( createdBy );
  }

  const createdDateRange = getValue( 'createdDateRange' );
  if ( createdDateRange ) {
    try {
      const parsed = JSON.parse( createdDateRange ) as { start: string; end: string };
      setCreationDate( [
        dayjs( parsed.start, FORMAT_DATE_API ).toDate(),
        dayjs( parsed.end, FORMAT_DATE_API ).toDate(),
      ] );
    } catch ( error ) {
      logger.warn( 'Invalid createdDateRange in saved extraction view filters', { raw: createdDateRange, error } );
    }
  }

  const deliveryDateRange = getValue( 'deliveryDateRange' );
  if ( deliveryDateRange ) {
    try {
      const parsed = JSON.parse( deliveryDateRange ) as { start: string; end: string };
      setDeliveryDate( [
        dayjs( parsed.start, FORMAT_DATE_API ).toDate(),
        dayjs( parsed.end, FORMAT_DATE_API ).toDate(),
      ] );
    } catch ( error ) {
      logger.warn( 'Invalid deliveryDateRange in saved extraction view filters', { raw: deliveryDateRange, error } );
    }
  }

  const archivedDateRange = getValue( 'archivedDateRange' );
  if ( archivedDateRange ) {
    try {
      const parsed = JSON.parse( archivedDateRange ) as { start: string; end: string };
      setArchivedDate( [
        dayjs( parsed.start, FORMAT_DATE_API ).toDate(),
        dayjs( parsed.end, FORMAT_DATE_API ).toDate(),
      ] );
    } catch ( error ) {
      logger.warn( 'Invalid archivedDateRange in saved extraction view filters', { raw: archivedDateRange, error } );
    }
  }

  const completionDateRange = getValue( 'completionDateRange' );
  if ( completionDateRange ) {
    try {
      const parsed = JSON.parse( completionDateRange ) as { start: string; end: string };
      setCompletionDate( [
        dayjs( parsed.start, FORMAT_DATE_API ).toDate(),
        dayjs( parsed.end, FORMAT_DATE_API ).toDate(),
      ] );
    } catch ( error ) {
      logger.warn( 'Invalid completionDateRange in saved extraction view filters', { raw: completionDateRange, error } );
    }
  }

  const multipleSheetsValue = getValue( 'multipleSheets' );
  if ( multipleSheetsValue === 'multi_sheets' || multipleSheetsValue === 'one_sheet' ) {
    setMultipleSheet( multipleSheetsValue );
  }
}

function buildCurrentFiltersAndColumnsHelper( {
  selectedClients,
  selectedSites,
  selectedSectors,
  selectedEventManagers,
  selectedParticipants,
  selectedThemes,
  selectedCreators,
  creationDate,
  deliveryDate,
  archivedDate,
  completionDate,
  hasMultipleSheet,
  selectedColumns,
}: {
  selectedClients: Option<Client>[];
  selectedSites: Option<Site>[];
  selectedSectors: Option<Sector>[];
  selectedEventManagers: Option<AdminTableWithSitesApiResponse>[];
  selectedParticipants: Option<Participant>[];
  selectedThemes: Option<ApiTheme>[];
  selectedCreators: Option<Participant>[];
  creationDate: Date[];
  deliveryDate: Date[];
  archivedDate: Date[];
  completionDate: Date[];
  hasMultipleSheet: string;
  selectedColumns: EventTableColumnKey[];
} ): { filters: ExtractionSavedViewFilterInput[]; columns: ExtractionSavedViewColumnInput[] } {
  const filters: ExtractionSavedViewFilterInput[] = [];

  if ( selectedClients.length > 0 ) {
    filters.push( {
      key: 'clientIds',
      value: JSON.stringify( selectedClients.map( client => { return client.object.id; } ) ),
    } );
  }

  if ( selectedSites.length > 0 ) {
    filters.push( {
      key: 'siteIds',
      value: JSON.stringify( selectedSites.map( site => { return site.object.id; } ) ),
    } );
  }

  if ( selectedSectors.length > 0 ) {
    filters.push( {
      key: 'sectorIds',
      value: JSON.stringify( selectedSectors.map( sector => { return sector.object.id; } ) ),
    } );
  }

  if ( selectedEventManagers.length > 0 ) {
    filters.push( {
      key: 'eventManagerIds',
      value: JSON.stringify( selectedEventManagers.map( em => { return em.object.id; } ) ),
    } );
  }

  if ( selectedParticipants.length > 0 ) {
    filters.push( {
      key: 'participantIds',
      value: JSON.stringify( selectedParticipants.map( participant => { return participant.object.id; } ) ),
    } );
  }

  if ( selectedThemes.length > 0 ) {
    filters.push( {
      key: 'themeIds',
      value: JSON.stringify( selectedThemes.map( theme => { return theme.object.id; } ) ),
    } );
  }

  if ( selectedCreators.length > 0 ) {
    filters.push( {
      key: 'createdBy',
      value: selectedCreators.map( creator => { return creator.object.id; } )[ 0 ],
    } );
  }

  if ( creationDate.length === 2 ) {
    filters.push( {
      key: 'createdDateRange',
      value: JSON.stringify( {
        start: dayjs( creationDate[ 0 ] ).format( FORMAT_DATE_API ),
        end: dayjs( creationDate[ 1 ] ).format( FORMAT_DATE_API ),
      } ),
    } );
  }

  if ( deliveryDate.length === 2 ) {
    filters.push( {
      key: 'deliveryDateRange',
      value: JSON.stringify( {
        start: dayjs( deliveryDate[ 0 ] ).format( FORMAT_DATE_API ),
        end: dayjs( deliveryDate[ 1 ] ).format( FORMAT_DATE_API ),
      } ),
    } );
  }

  if ( archivedDate.length === 2 ) {
    filters.push( {
      key: 'archivedDateRange',
      value: JSON.stringify( {
        start: dayjs( archivedDate[ 0 ] ).format( FORMAT_DATE_API ),
        end: dayjs( archivedDate[ 1 ] ).format( FORMAT_DATE_API ),
      } ),
    } );
    filters.push( {
      key: 'archived',
      value: 'true',
    } );
  }

  if ( completionDate.length === 2 ) {
    filters.push( {
      key: 'completionDateRange',
      value: JSON.stringify( {
        start: dayjs( completionDate[ 0 ] ).format( FORMAT_DATE_API ),
        end: dayjs( completionDate[ 1 ] ).format( FORMAT_DATE_API ),
      } ),
    } );
    filters.push( {
      key: 'completed',
      value: 'true',
    } );
  }

  filters.push( {
    key: 'multipleSheets',
    value: hasMultipleSheet,
  } );

  const columns: ExtractionSavedViewColumnInput[] = selectedColumns.map( key => {
    return {
      key,
      value: key,
    };
  } );

  return { filters, columns };
}

export default ExtractionView;

interface InitExtractionViewParams {
  token: string | null;
  dispatch: ReturnType<typeof useDispatch>;
  user: RootState['user']['value'];
  t: ( key: string ) => string;
  setClientsOption: React.Dispatch<React.SetStateAction<Option<Client>[]>>;
  setAllClients: React.Dispatch<React.SetStateAction<Client[]>>;
  setAllSites: React.Dispatch<React.SetStateAction<Site[]>>;
  setSitesOption: React.Dispatch<React.SetStateAction<GroupedOption<Site>[]>>;
  setSelectedSites: React.Dispatch<React.SetStateAction<Option<Site>[]>>;
  setAllSectors: React.Dispatch<React.SetStateAction<Sector[]>>;
  setSectorsOption: React.Dispatch<React.SetStateAction<GroupedOption<Sector>[]>>;
  setSelectedSectors: React.Dispatch<React.SetStateAction<Option<Sector>[]>>;
  setAllEventManagers: React.Dispatch<React.SetStateAction<AdminTableWithSitesApiResponse[]>>;
  setEventManagersOption: React.Dispatch<React.SetStateAction<GroupedOption<AdminTableWithSitesApiResponse>[]>>;
  setSelectedEventManagers: React.Dispatch<React.SetStateAction<Option<AdminTableWithSitesApiResponse>[]>>;
  setAllThemes: React.Dispatch<React.SetStateAction<Theme[]>>;
  setIsFirstRender: React.Dispatch<React.SetStateAction<boolean>>;
  selectedClients: Option<Client>[];
  selectedSites: Option<Site>[];
  selectedSectors: Option<Sector>[];
  allSites: Site[];
  allSectors: Sector[];
  multiSheetGrouping: 'client' | 'site' | 'sector' | 'table';
}

async function initExtractionView( params: InitExtractionViewParams ): Promise<void> {
  const {
    token,
    dispatch,
    user,
    t,
    setClientsOption,
    setAllClients,
    setAllSites,
    setSitesOption,
    setSelectedSites,
    setAllSectors,
    setSectorsOption,
    setSelectedSectors,
    setAllEventManagers,
    setEventManagersOption,
    setSelectedEventManagers,
    setAllThemes,
    setIsFirstRender,
    selectedClients,
    selectedSites,
    selectedSectors,
    allSites,
    allSectors,
    multiSheetGrouping,
  } = params;

  const clientsFetched = await CommonClientService.getAllowedClients( null, token, dispatch );
  const updatedClientsOptions = CommonGlobalService.toOptions( clientsFetched );
  setClientsOption( updatedClientsOptions );
  setAllClients( clientsFetched );

  let sitesFetched: Site[] = [];
  if ( clientsFetched.length > 0 ) {
    const allSitesUpdated = await CommonSiteService.getAllowedSitesByClientsId( clientsFetched.map( client => { return client.id; } ), token, dispatch );
    setAllSites( allSitesUpdated );
    sitesFetched = allSitesUpdated;
  } else {
    setAllSites( [] );
  }
  const groupedSites: GroupedOption<Site>[] = [];
  const allSitesOptions = CommonGlobalService.toOptions( sitesFetched );
  for ( const [ clientId, options ] of Object.entries( CommonGlobalService.groupBy( allSitesOptions, option => { return option.object.clientId; } ) ) ) {
    const optionSites = options ?? [];
    CommonUtilsService.sortByKey( optionSites, 'label', 'asc' );
    groupedSites.push( {
      label: clientsFetched.find( client => { return client.id === clientId; } )?.name ?? t( 'common:error.missing_client' ),
      options: optionSites
    } );
  }
  setSitesOption( groupedSites );
  let updatedSites: Option<Site>[] = [];
  if ( groupedSites.length === 1 && groupedSites[ 0 ].options.length === 1 ) {
    updatedSites = [ groupedSites[ 0 ].options[ 0 ] ];
    setSelectedSites( updatedSites );
  }

  let sectorsFetched: Sector[] = [];
  const allSectorsUpdated = await CommonSectorService.getAllowedSectors( null, token, dispatch );
  setAllSectors( allSectorsUpdated );
  sectorsFetched = allSectorsUpdated;
  const groupedSectors: GroupedOption<Sector>[] = [];
  const allSectorsOptions = CommonGlobalService.toOptions( sectorsFetched );
  for ( const [ siteId, options ] of Object.entries( CommonGlobalService.groupBy( allSectorsOptions, option => { return option.object.siteId; } ) ) ) {
    const optionSectors = options ?? [];
    CommonUtilsService.sortByKey( optionSectors, 'label', 'asc' );
    groupedSectors.push( {
      label: sitesFetched.find( site => { return site.id === siteId; } )?.name ?? t( 'common:error.missing_site' ),
      options: optionSectors
    } );
  }
  setSectorsOption( groupedSectors );
  let updatedSectors: Option<Sector>[] = [];
  if ( groupedSectors.length === 1 && groupedSectors[ 0 ].options.length === 1 ) {
    updatedSectors = [ groupedSectors[ 0 ].options[ 0 ] ];
    setSelectedSectors( updatedSectors );
  }

  if ( user?.id ) {
    const eventManagersFetched = user.readAccessSectors
      ? await EventManagerService.getEventManagersWhereUserIsParticipantOrAdminForExtraction( user.id, token, dispatch )
      : await EventManagerService.getEventManagersWhereUserIsParticipantOrAdmin( user.id, token, dispatch );

    setAllEventManagers( eventManagersFetched );
    const groupedEventManagers = buildEventManagersOptions( eventManagersFetched, selectedClients, selectedSites, selectedSectors, allSites, allSectors, multiSheetGrouping, t );
    setEventManagersOption( groupedEventManagers );

    if ( groupedEventManagers.length === 1 && groupedEventManagers[ 0 ].options.length === 1 ) {
      setSelectedEventManagers( [ groupedEventManagers[ 0 ].options[ 0 ] ] );
    }
    const themesForExtraction = await ThemeService.getThemesForExtractionFilters( {}, token, dispatch );
    setAllThemes( themesForExtraction );
  }

  setIsFirstRender( false );
}